import {
  CURRENT_CONTENT_SCHEMA_VERSION,
  type DomainDocument,
  type EntityId,
} from "@note-app/contracts";

export type QuickCaptureMode = "note" | "task" | "link";

export interface QuickCaptureTitleInput {
  readonly title: string;
  readonly body: string;
  readonly fallback: string;
  readonly inferredTitleLimit?: number;
}

export interface PrepareQuickCaptureInput {
  readonly title: string;
  readonly body: string;
  readonly fallbackTitle: string;
  readonly nextId: () => EntityId;
  readonly mode?: QuickCaptureMode;
}

export interface PreparedQuickCapture {
  readonly title: string;
  readonly mode: QuickCaptureMode;
  readonly document: DomainDocument;
}

export class QuickCaptureValidationError extends RangeError {
  public readonly code: "link-required";

  public constructor(code: "link-required") {
    super("Link captures require a valid http:// or https:// address.");
    this.name = "QuickCaptureValidationError";
    this.code = code;
  }
}

/**
 * Applies the same predictable quick-capture title rule on every platform.
 * Explicit titles are preserved after trimming; otherwise the first body line
 * becomes the title without splitting Unicode code points.
 */
export function deriveQuickCaptureTitle({
  title,
  body,
  fallback,
  inferredTitleLimit = 80,
}: QuickCaptureTitleInput): string {
  const explicitTitle = title.trim();
  if (explicitTitle) return explicitTitle;

  const firstLine = body.trim().split(/\r?\n/u, 1)[0]?.trim() ?? "";
  if (firstLine) {
    return Array.from(firstLine).slice(0, inferredTitleLimit).join("");
  }
  return fallback.trim();
}

/**
 * Builds the persisted document for every quick-capture entry point. Keeping
 * this at the application boundary prevents desktop, web, and mobile shells
 * from silently assigning different semantics to the same capture mode.
 */
export function prepareQuickCapture({
  title,
  body,
  fallbackTitle,
  nextId,
  mode = "note",
}: PrepareQuickCaptureInput): PreparedQuickCapture {
  const normalizedTitle = title.trim();
  const normalizedBody = body.trim();
  const link = mode === "link"
    ? extractQuickCaptureWebLink(normalizedBody)
    : undefined;

  if (mode === "link" && link === undefined) {
    throw new QuickCaptureValidationError("link-required");
  }

  const titleSource = link === undefined
    ? normalizedBody
    : normalizedBody.replace(link.href, "").trim() || link.hostname;
  const blockBody = mode === "task"
    ? normalizedBody || normalizedTitle
    : normalizedBody;
  const blocks: DomainDocument["blocks"] = blockBody.length === 0
    ? []
    : link !== undefined
      ? [{
          id: nextId(),
          type: "bookmark",
          props: {
            blockWidth: "default",
            url: link.href,
            title: normalizedTitle || link.hostname,
            description: normalizedBody.replace(link.href, "").trim(),
          },
          content: null,
          children: [],
        }]
      : [{
          id: nextId(),
          type: mode === "task" ? "checkListItem" : "paragraph",
          props: mode === "task" ? { checked: false } : {},
          content: blockBody,
          children: [],
        }];

  return {
    mode,
    title: deriveQuickCaptureTitle({
      title: normalizedTitle,
      body: titleSource,
      fallback: fallbackTitle,
    }),
    document: {
      schemaVersion: CURRENT_CONTENT_SCHEMA_VERSION,
      blocks,
    },
  };
}

export function extractQuickCaptureWebLink(
  value: string,
): { readonly href: string; readonly hostname: string } | undefined {
  const href = value.match(/https?:\/\/[^\s<>"'，。！？、；）】》]+/iu)?.[0];
  if (href === undefined) return undefined;
  try {
    const url = new URL(href);
    return url.protocol === "http:" || url.protocol === "https:"
      ? { href, hostname: url.hostname }
      : undefined;
  } catch {
    return undefined;
  }
}
